import {
  SelectOption,
} from "@veneer/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import UsersManagementService from "../../services/userManagementApi/usersManagement.service";
import { CellFormulaValue, CellHyperlinkValue, Workbook } from "exceljs";
import { CategoriesFundEnum } from "../../models/enum/userManagementApi/CategoriesFundEnum";
import { CategoriesEnum } from "../../models/enum/userManagementApi/CategoriesEnum";
import { IManageUser } from "../../models/input/userApi/IManageUser";

export const useUsersManagementMassive = () => {
  type SelectValue = number | string | boolean;

  const [t] = useTranslation("global");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [openModalCreateDone, setOpenModalCreateDone] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  
  const [actionTypeSelected, setActionTypeSelected] = useState<SelectValue[]>([]);
  const [disabledDownloadTemplate, setDisabledDownloadTemplate] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [buttonSaveDisabled, setButtonSaveDisabled] = useState(true);
  const [jsonUsers, setJsonUsers] = useState<IManageUser[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [messageError, setErrorMessage] = useState<string>("");
  const [errorFilePicker, setErrorFilePicker] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalHelp, setOpenModalHelp] = useState(false);
  const [actionTypeIDFile, setActionTypeIDFile] = useState(0);

  const optionsTypeAction: SelectOption[] =  [
    { value: 1, label: t("header.create") },
    { value: 2, label: t("header.update") }
  ];

  const enum actionsEnum {
    create = 1,
    update = 2
  }

  const handleDownloadTemplate = () => {
    setLoading(true)
    UsersManagementService.getTempateUsersManagementMassive(Number(actionTypeSelected), Number(languageID.value))
      .then((r: Blob | any) => {
        try {
          if (r?.statusCode === 401) {
            console.error('Error:', "Download failed");
          } else if (r instanceof Blob) {
            try {

              let fileName = 'template_{action}_users_management_massive';

              switch (Number(actionTypeSelected)) {
                case 1: fileName = fileName.replace('{action}', 'create');
                  break;
                case 2: fileName = fileName.replace('{action}', 'update');
                  break;
                default: fileName = fileName.replace('{action}', '');
              }

              const url = window.URL.createObjectURL(r);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', fileName + '.xlsx');
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error('Error:', error);
            }
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          setLoading(false)
          setActionTypeSelected([])
        }
      })
  }

  useEffect(() => {
    if (actionTypeSelected.length > 0) {
      setDisabledDownloadTemplate(false);
    } else {
      setDisabledDownloadTemplate(true);
    }
  }, [actionTypeSelected]);

  const handleCreateUsers = () => {
    setActionTypeIDFile(0);
    setOpenModalCreate(true);
    setFileName("");
    setErrorFilePicker(false);
    setButtonSaveDisabled(true);
  };

  const handleOnClickExit = () => {
    setFileName("");
    setOpenModalCreate(false);
    setButtonSaveDisabled(true);
  };

  const categorySelectedApplyForPrintFund = (
    categorySelected: number
  ): boolean => {
    return (
      categorySelected === CategoriesEnum.Supplies ||
      categorySelected === CategoriesEnum.Printing ||
      categorySelected === CategoriesEnum.DYP ||
      categorySelected === CategoriesEnum.PPS
    );
  };

  const categorySelectedApplyForPCFund = (
    categorySelected: number
  ): boolean => {
    return (
      categorySelected === CategoriesEnum.Computing ||
      categorySelected === CategoriesEnum.PPS
    );
  };

  const getCategoryFundID = (isPrint: boolean, isPC: boolean): number => {
    if (isPrint && isPC) {
      return CategoriesFundEnum.Both;
    } else if (isPrint) {
      return CategoriesFundEnum.Print;
    } else if (isPC) {
      return CategoriesFundEnum.PC;
    } else {
      return 0;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = e.target;
    if (!fileInput.files) {
      setErrorMessage(t("modal.errorLoadingFile"));
      setErrorFilePicker(true);
      return;
    }
    
    const file = fileInput.files[0];
    setFileName(file.name);
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();
    
    if (fileType?.toLowerCase() !== "xlsx") {
      setErrorMessage(t("header.messageXLSX"));
      setErrorFilePicker(true);
      return;
    }
    
    setLoadingModal(true);
    setErrorFilePicker(false);
    reader.onload = async (event) => {
      const data = event.target?.result as ArrayBuffer;
      const workbook = new Workbook();

      try {
        await workbook.xlsx.load(data);
        const hiddenSheet = workbook.getWorksheet("Data");
        const advisorsSheet = workbook.getWorksheet("Advisors");
        var actionTypeID = 0; // 1 for create and 2 for update
        var countriesMap: Record<string, number> = {};
        var citiesMap: Record<string, number> = {};
        var timesMap: Record<string, number> = {};
        var categoriesMap: Record<string, number> = {};
        var partnersMap: Record<string, number> = {};
        var fundsMap: Record<string, number> = {};
        var fundsHPMap: Record<string, number> = {};
        var supervisorsMap: Record<string, number> = {};
        var rolesMap: Record<string, number> = {};
        var advisorsMap: Record<string, number> = {};
        const rows: any[] = [];

        if (hiddenSheet) {
          
          actionTypeID = Number(hiddenSheet.getCell("U1").value);
          setActionTypeIDFile(actionTypeID);

          // set map of data list
          // countries list
          let row = 1;
          while (hiddenSheet.getCell(`A${row}`).value && hiddenSheet.getCell(`B${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`A${row}`).value);
            const propertyName = hiddenSheet.getCell(`B${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !countriesMap[propertyName?.trim()]) {
              countriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // cities list
          row = 1;
          while (hiddenSheet.getCell(`E${row}`).value && hiddenSheet.getCell(`F${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`E${row}`).value);
            const propertyName = hiddenSheet.getCell(`F${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !citiesMap[propertyName?.trim()]) {
              citiesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // times list
          row = 1;
          while (hiddenSheet.getCell(`G${row}`).value && hiddenSheet.getCell(`H${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`G${row}`).value);
            const propertyName = hiddenSheet.getCell(`H${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !timesMap[propertyName?.trim()]) {
              timesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // categories list
          row = 1;
          while (hiddenSheet.getCell(`I${row}`).value && hiddenSheet.getCell(`J${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`I${row}`).value);
            const propertyName = hiddenSheet.getCell(`J${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !categoriesMap[propertyName?.trim()]) {
              categoriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // categories list
          row = 1;
          while (hiddenSheet.getCell(`I${row}`).value && hiddenSheet.getCell(`J${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`I${row}`).value);
            const propertyName = hiddenSheet.getCell(`J${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !categoriesMap[propertyName?.trim()]) {
              categoriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // partners list
          row = 1;
          while (hiddenSheet.getCell(`K${row}`).value && hiddenSheet.getCell(`L${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`K${row}`).value);
            const propertyName = hiddenSheet.getCell(`L${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !partnersMap[propertyName?.trim()]) {
              partnersMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // funds list
          row = 1;
          while (hiddenSheet.getCell(`M${row}`).value && hiddenSheet.getCell(`N${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`M${row}`).value);
            const propertyName = hiddenSheet.getCell(`N${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !fundsMap[propertyName?.trim()]) {
              fundsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // funds HP list
          row = 1;
          while (hiddenSheet.getCell(`O${row}`).value && hiddenSheet.getCell(`P${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`O${row}`).value);
            const propertyName = hiddenSheet.getCell(`P${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !fundsHPMap[propertyName?.trim()]) {
              fundsHPMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // supervisors list
          row = 1;
          while (hiddenSheet.getCell(`Q${row}`).value && hiddenSheet.getCell(`R${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`Q${row}`).value);
            const propertyName = hiddenSheet.getCell(`R${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !supervisorsMap[propertyName?.trim()]) {
              supervisorsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // roles list
          row = 1;
          while (hiddenSheet.getCell(`S${row}`).value && hiddenSheet.getCell(`T${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`S${row}`).value);
            const propertyName = hiddenSheet.getCell(`T${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !rolesMap[propertyName?.trim()]) {
              rolesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }
        }

        if (advisorsSheet && actionTypeID === actionsEnum.update) {
          
          let row = 1;
          // set map of data list
          while (advisorsSheet.getCell(`A${row}`).value && advisorsSheet.getCell(`B${row}`).value) {
            const propertyID = Number(advisorsSheet.getCell(`A${row}`).value);
            const propertyName = advisorsSheet.getCell(`B${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !advisorsMap[propertyName?.trim()]) {
              advisorsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }
        }

        if (!actionTypeID) {
          setErrorMessage(t("header.messageFormatError"));
          setErrorFilePicker(true);
          return;
        }

        const sheet = workbook.worksheets[0];
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) {
            return;
          }

          let advisorName, name, lastName, document, country, city, phone, email, supervisor, role, time, category,
          partnerPrint, fundPrint, fundHPPrint, partnerPC, fundPC, fundHPPC, feePC, feePrint;

          let yesValue = t("header.yes");
          let feePCLabel = getValueCellTemplate(row.getCell(19).value);
          let feePCValue = (feePCLabel === yesValue) ? 1 : 0;

          let feePrintLabel = getValueCellTemplate(row.getCell(20).value);
          let feePrintValue = (feePrintLabel === yesValue) ? 1 : 0;

          if (actionTypeID === actionsEnum.create) {

            name = getValueCellTemplate(row.getCell(1).value);
            lastName = getValueCellTemplate(row.getCell(2).value);
            document = getValueCellTemplate(row.getCell(3).value);
            country = getValueCellTemplate(row.getCell(4).value);
            city = getValueCellTemplate(row.getCell(6).value);
            phone = getValueCellTemplate(row.getCell(7).value);
            email = getValueCellTemplate(row.getCell(8).value);
            supervisor = getValueCellTemplate(row.getCell(9).value);
            role = getValueCellTemplate(row.getCell(10).value);
            time = getValueCellTemplate(row.getCell(11).value);
            category = getValueCellTemplate(row.getCell(12).value);
            partnerPrint = getValueCellTemplate(row.getCell(13).value);
            fundPrint = getValueCellTemplate(row.getCell(14).value);
            fundHPPrint = getValueCellTemplate(row.getCell(15).value);
            partnerPC = getValueCellTemplate(row.getCell(16).value);
            fundPC = getValueCellTemplate(row.getCell(17).value);
            fundHPPC = getValueCellTemplate(row.getCell(18).value);
            feePC = feePCValue;
            feePrint = feePrintValue;
          }

          if (actionTypeID === actionsEnum.update && !getValueCellTemplate(row.getCell(1).value)) {
            return;
          }
          
          if (actionTypeID === actionsEnum.update) {
            yesValue = t("header.yes");
            feePCLabel = getValueCellTemplate(row.getCell(18).value);
            feePCValue = (feePCLabel === yesValue) ? 1 : 0;

            feePrintLabel = getValueCellTemplate(row.getCell(19).value);
            feePrintValue = (feePrintLabel === yesValue) ? 1 : 0;

            advisorName = getValueCellTemplate(row.getCell(1).value);
            document = getValueCellTemplate(row.getCell(2).value);
            country = getValueCellTemplate(row.getCell(3).value);
            city = getValueCellTemplate(row.getCell(5).value);
            phone = getValueCellTemplate(row.getCell(6).value);
            email = getValueCellTemplate(row.getCell(7).value);
            supervisor = getValueCellTemplate(row.getCell(8).value);
            role = getValueCellTemplate(row.getCell(9).value);
            time = getValueCellTemplate(row.getCell(10).value);
            category = getValueCellTemplate(row.getCell(11).value);
            partnerPrint = getValueCellTemplate(row.getCell(12).value);
            fundPrint = getValueCellTemplate(row.getCell(13).value);
            fundHPPrint = getValueCellTemplate(row.getCell(14).value);
            partnerPC = getValueCellTemplate(row.getCell(15).value);
            fundPC = getValueCellTemplate(row.getCell(16).value);
            fundHPPC = getValueCellTemplate(row.getCell(17).value);
            feePC = feePCValue;
            feePrint = feePrintValue;
          }

          const requirePrintFund = categorySelectedApplyForPrintFund(
            Number(categoriesMap[category || 0])
          );
          const requirePCFund = categorySelectedApplyForPCFund(
            Number(categoriesMap[category || 0])
          );

          if (((actionTypeID === actionsEnum.update && advisorName && advisorsMap[advisorName.trim()]) || actionTypeID === actionsEnum.create) &&
            ((actionTypeID === actionsEnum.create && name && lastName) || actionTypeID === actionsEnum.update) &&
            document && phone && email &&
            country && countriesMap[country] &&
            city && citiesMap[city] &&
            supervisor && supervisorsMap[supervisor] &&
            role && rolesMap[role] &&
            time && timesMap[time] &&
            category && categoriesMap[category] &&
            ((requirePrintFund && (partnerPrint && partnersMap[partnerPrint]) &&
            (fundPrint && fundsMap[fundPrint])) || !requirePrintFund) &&
            ((requirePCFund && (partnerPC && partnersMap[partnerPC]) &&
            (fundPC && fundsMap[fundPC])) || !requirePCFund)
          ) {
            
            const categoryFundID = getCategoryFundID(requirePrintFund, requirePCFund);

            const rowData: IManageUser = {
              UserID: 0,
              Name: name,
              LastName: lastName,
              NumberID: document,
              CountryID: countriesMap[country],
              CityID: citiesMap[city],
              Phone: phone.replace(/\D/g, ""),
              Email: email,
              SupervisorID: supervisorsMap[supervisor],
              RoleID: rolesMap[role],
              TimeID: timesMap[time],
              CategoryID: categoriesMap[category],
              PartnerPrintID: partnerPrint && requirePrintFund ? partnersMap[partnerPrint] : 0,
              FundPrintID: fundPrint && requirePrintFund ? fundsMap[fundPrint] : 0,
              FundHpPrintID: fundHPPrint && requirePrintFund ? fundsHPMap[fundHPPrint] : 0,
              PartnerPCID: partnerPC && requirePCFund ? partnersMap[partnerPC] : 0,
              FundPCID: fundPC && requirePCFund ? fundsMap[fundPC] : 0,
              FundHpPCID: fundHPPC && requirePCFund ? fundsHPMap[fundHPPC] : 0,
              CategoryFundID: categoryFundID,
              FeePC: feePC ? Number(feePC) : 0,
              FeePrint: feePrint ? Number(feePrint) : 0,
            };

            if (actionTypeID === actionsEnum.update) {
              rowData.UserID = advisorName ? advisorsMap[advisorName] : 0;
            }

            rows.push(rowData);
          } else {
            setButtonSaveDisabled(true);
            setErrorFilePicker(true);
            setErrorMessage(t("modal.invalidDataUsersMassive").replace('{0}', rowNumber.toString()));
          }
        });
        setJsonUsers(rows);
      } catch (error) {
        setErrorFilePicker(true);
        setButtonSaveDisabled(true);
        setErrorMessage(t("modal.errorLoadingFile"));
      }

      setLoadingModal(false);
    };

    reader.readAsArrayBuffer(file);
    setButtonSaveDisabled(false);
  };

  const onClearFilePicker = () => {
    setFileName("");
    setErrorFilePicker(false);
    setButtonSaveDisabled(true);
  };

  const submitUsers = async () => {

    setLoadingModal(true);
    interface DataItem {
      userID: number;
      email?: string;
      numberID?: string;
      actionTypeID: number;
      result: number;
      message?: string;
    }

    try {
      const response: any[] = await UsersManagementService.manageUsersInBulk(jsonUsers, actionTypeIDFile);
      
      if (!response) {
        setOpenModalCreate(false);
        setOpenModalError(true);
        return;
      }

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Datos");

      worksheet.columns = [
        { header: t("header.userId"), key: "userID", width: 15 },
        { header: t("header.email"), key: "email", width: 15 },
        { header: t("header.document"), key: "numberID", width: 15 },
        { header: t("modal.message"), key: "message", width: 30 },
      ];

      response.forEach((item: DataItem) => {

        if (item.actionTypeID === actionsEnum.create) {

          item.message = item.result === 1 ? t("modal.userCreated")
          : t("modal.userNotCreated"); 
        }

        if (item.actionTypeID === actionsEnum.update) {

          item.message = item.result === 1 ? t("modal.updateInfo")
          : t("modal.informationNotUpdated"); 
        }


        const row = worksheet.addRow(item);

        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "00B0F0" } };
        cell.alignment = { horizontal: "center" };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
        cell.font = { bold: true };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);

      setOpenModalCreate(false);
      setOpenModalCreateDone(true);

      const a = document.createElement("a");
      a.href = url;
      a.download = "users_result.xlsx";
      a.click();

      URL.revokeObjectURL(url);
      setLoadingModal(false);
    } catch (error) {
      setOpenModalCreate(false);
      setOpenModalError(true);
      setLoadingModal(false);
    }
  };

  const getValueCellTemplate = (cellValue: any): string | undefined => {

    if (typeof cellValue === 'string' || typeof cellValue === 'number') {
      return cellValue?.toString().trim();
    }

    if ((cellValue as CellHyperlinkValue)?.text) {
      return (cellValue as CellHyperlinkValue)?.text?.toString().trim();
    }

    if ((cellValue as CellFormulaValue)?.result) {
      return (cellValue as CellFormulaValue)?.result?.toString().trim();
    }

    return;
  }

  return {
    optionsTypeAction,
    actionTypeSelected,
    setActionTypeSelected,
    handleCreateUsers,
    setOpenModalHelp,
    openModalHelp,
    loading,
    disabledDownloadTemplate,
    handleDownloadTemplate,
    loadingModal,
    openModalCreate,
    handleOnClickExit,
    buttonSaveDisabled,
    handleFileChange,
    fileName,
    errorFilePicker,
    messageError,
    submitUsers,
    onClearFilePicker,
    openModalCreateDone,
    openModalError,
    setOpenModalCreateDone,
    setOpenModalError
  };
}